import React, { memo, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';

// styles
import './style.scss';

import WaveSurfer from 'wavesurfer.js';

import useDeviceDetect from 'utils/useDeviceDetect';

// create a global array to hold our WaveSurfers
var WaveSurfers = [];

const peaksInit = [
  0.0198,
  0.2137,
  0.2888,
  0.2313,
  0.15,
  0.1942,
  0.2138,
  0.2358,
  0.1195,
  0.1591,
  0.2599,
  0.2742,
  0.1447,
  0.2328,
  0.1878,
  0.1988,
  0.1645,
  0.1218,
  0.2005,
  0.2828,
  0.2051,
  0.1664,
  0.1181,
  0.1621,
  0.2966,
  0.189,
  0.246,
  0.2445,
  0.1621,
  0.1618,
  0.189,
  0.2354,
  0.1561,
  0.1638,
  0.2799,
  0.0923,
  0.1659,
  0.1675,
  0.1268,
  0.0984,
  0.0997,
  0.1248,
  0.1495,
  0.1431,
  0.1236,
  0.1755,
  0.1183,
  0.1349,
  0.1018,
  0.1109,
  0.1833,
  0.1813,
  0.1422,
  0.0961,
  0.1191,
  0.0791,
  0.0631,
  0.0315,
  0.0157,
  0.0166,
  0.0108,
];

const MediaPlayer = ({
  key,
  type,
  url,
  date,
  className,
  duration,
  isBlob,
  blobFile,
  ...rest
}) => {
  const childClassNames = classNames(`media-player-wrapper ${type}`, className);
  const { isMobile } = useDeviceDetect();

  const waveformRef = useRef(null);
  const wavesurfer = useRef(null);

  const [playing, setPlay] = useState(false);
  const [pause, setPause] = useState(false);
  const [loading, setLoading] = useState(false);
  const [audioDuration, setAudioDuration] = useState(!!duration ? duration : 0);
  const [currentTime, setCurrentTime] = useState(0);
  const [loadingPercent, setLoadingPercent] = useState(0);

  const formWaveSurferOptions = ref => {
    let mainOptions = {
      container: ref,
      backend: 'MediaElement', // WebAudio, MediaElement, MediaElementWebAudio
      mediaType: 'audio', // 'audio' or 'video'. Only used with backend MediaElement.
      waveColor: '#b2ced4',
      progressColor: '#CBA348',
      height: 30,
      // If true, normalize by the maximum peak instead of 1.0.
      normalize: true,
      cursorColor: 'transparent',
      barWidth: 1,
      barRadius: 0,
      // Use the PeakCache to improve rendering speed of large waveforms.
      partialRender: true,
      hideScrollbar: true,
      splitChannels: false,
    };
    return mainOptions;
  };

  // create new WaveSurfer instance
  // On component mount and when url changes
  useEffect(() => {
    const options = formWaveSurferOptions(waveformRef.current);
    // Init
    wavesurfer.current = WaveSurfer.create(options);

    // add the WaveSurfer to the global array so we can keep up with it
    WaveSurfers.push(wavesurfer.current);

    // if (!audioData) return;
    // if (typeof audioData === 'string') {
    //   wavesurfer.current?.load(audioData, peaksInit); // must be a URL
    // } else {
    //   wavesurfer.current?.loadBlob(audioData); //must be a Blob
    // }

    if (!!isBlob && !!blobFile) {
      // Load audio from Blob
      wavesurfer.current.loadBlob(blobFile);
    } else {
      // Load audio from URL and set peaks
      wavesurfer.current.load(url, peaksInit, 'metadata');
    }

    wavesurfer.current.on('loading', e => {
      setLoading(true);
      setLoadingPercent(e);
    });
    wavesurfer.current.on('pause', function() {
      setPause(true);
    });
    wavesurfer.current.on('play', e => {
      const dur = wavesurfer.current.getDuration();
      if (dur != 'Infinity') {
        setAudioDuration(dur);
      }
      setPlay(true);
      setPause(false);
    });

    // 'waveform-ready' - Fires after the waveform is drawn when using the MediaElement backend.
    // If you're using the WebAudio backend, you can use 'ready'.
    wavesurfer.current.on('ready', () => {
      setLoading(false);

      wavesurfer.current.drawBuffer();

      const dur = wavesurfer.current.getDuration();
      if (dur != 'Infinity') {
        setAudioDuration(dur);
      }
    });

    // Show current time
    // if current time same as duration then stop playing
    wavesurfer.current.on('audioprocess', () => {
      const cDur = wavesurfer.current.getDuration();
      const cTime = wavesurfer.current.getCurrentTime();
      if (cDur != 'Infinity') {
        setAudioDuration(cDur);
      }
      !!cTime && setCurrentTime(cTime);
      setPause(false);
    });

    wavesurfer.current.on('finish', e => {
      setPlay(false);
      setPause(false);
      wsSetPause(wavesurfer.current, 'stop');
    });
  }, [url]);

  // Removes events, elements and disconnects Web Audio nodes when component unmount
  useEffect(() => {
    // destroy() – Removes events, elements and disconnects Web Audio nodes.
    // unAll() – Unsubscribes from all events.
    return () => wavesurfer.current.unAll();
  }, []);

  const formatTime = time => {
    return !!time
      ? [
          Math.floor((time % 3600) / 60), // minutes
          ('00' + Math.floor(time % 60)).slice(-2), // seconds
        ].join(':')
      : '0:00';
  };

  const wsSetPause = (ws, type = 'pause') => {
    const parent = ws.container.parentNode.closest('.media-player-wrapper'),
      playBtn = parent.querySelector('.play-btn .play-pause-icon');

    type == 'pause' ? ws.pause() : ws.stop();
    ws.container.classList.remove('playing');

    if (playBtn) {
      playBtn.classList.remove('paused');
      playBtn.classList.add('play');
    }
  };

  const wsSetPlay = ws => {
    const parent = ws.container.parentNode.closest('.media-player-wrapper'),
      playBtn = parent.querySelector('.play-btn .play-pause-icon');

    ws.play();
    ws.container.classList.add('playing');

    if (playBtn) {
      playBtn.classList.remove('play');
      playBtn.classList.add('paused');
    }
  };

  // toggle play button
  const handlePlayPause = e => {
    if (loading === false) {
      const wavesurferContainer = wavesurfer.current.container,
        wavesurferParent = wavesurferContainer.parentNode.closest(
          '.media-player-wrapper',
        ),
        itemPlayBtn = wavesurferParent.querySelector(
          '.play-btn .play-pause-icon',
        );

      if (!playing || itemPlayBtn.classList.contains('play')) {
        setPlay(true);
        wsSetPlay(wavesurfer.current);

        // check all wavesurfer
        WaveSurfers.length > 1 &&
          WaveSurfers.map(waveSurferItem => {
            if (
              waveSurferItem.container.classList.contains('playing') &&
              waveSurferItem.container.id !== wavesurferContainer.id
            ) {
              wsSetPause(waveSurferItem, 'stop');
              waveSurferItem.unAll();
            }
          });
      } else if (playing || itemPlayBtn.classList.contains('paused')) {
        setPlay(false);
        wsSetPause(wavesurfer.current);
      }
    }
  };

  return (
    url && (
      <div className={childClassNames} {...rest}>
        <div
          id={`waveform-container-${new Date().getTime()}`}
          className="waveform-container"
        >
          <div
            id={`waveform-play-${new Date().getTime()}`}
            className="play-btn"
            onClick={handlePlayPause}
          >
            <span className={`play-pause-icon play`} />
          </div>

          <div
            id={`waveform-${new Date().getTime()}`}
            className="waveform"
            ref={waveformRef}
          />
        </div>
        <div className="waveform-details">
          {(playing || pause) && !!currentTime && (
            <span className="waveform-current-time">
              {formatTime(currentTime)}
            </span>
          )}
          {!!audioDuration && (
            <span className="waveform-duration">
              {formatTime(audioDuration)}
            </span>
          )}
        </div>
      </div>
    )
  );
};

MediaPlayer.defaultProps = {
  type: 'small',
  isBlob: false,
  duration: false,
};

MediaPlayer.propTypes = {
  type: PropTypes.oneOf(['small', 'feed']).isRequired,
  date: PropTypes.string,
  url: PropTypes.string.isRequired,
};

export default memo(MediaPlayer);
